import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { NotificationContainer } from './components/ui/NotificationContainer';
import { ProtectedRoute } from './components/layout/ProtectedRoute';
import { InvoiceOnlyRedirect } from './components/InvoiceOnlyRedirect';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { Login } from './pages/Login';
import { Dashboard } from './pages/Dashboard';
import { MainLayout } from './components/layout/MainLayout';
import { LeaveRequests } from './pages/LeaveRequests';
import { NewLeaveRequest } from './pages/NewLeaveRequest';
import { LeaveRequestDetail } from './pages/LeaveRequestDetail';
import { Team } from './pages/Team';
import { UserManagement } from './pages/UserManagement';
import { UserForm } from './pages/UserForm';
import { UserNipManagement } from './pages/UserNipManagement';
import { Reports } from './pages/Reports';
import { TeamCalendar } from './pages/TeamCalendar';
import SystemNotifications from './pages/SystemNotifications';
import DepartmentManagement from './pages/DepartmentManagement';
import Substitutions from './pages/Substitutions';
import LeaveTypeManagement from './pages/LeaveTypeManagement';
import EditLeaveRequest from './pages/EditLeaveRequest';
import NotificationCenter from './pages/NotificationCenter';
import { RoleManagement } from './pages/RoleManagement';
import { WorkflowManagement } from './pages/WorkflowManagement';
import { WorkflowDetail } from './pages/WorkflowDetail';
import { ForgotPassword } from './pages/ForgotPassword';
import { ResetPassword } from './pages/ResetPassword';
import { PasswordResetSuccess } from './pages/PasswordResetSuccess';
import { UserRedirect } from './components/UserRedirect';
import { TeamLeaveRequests } from './pages/TeamLeaveRequests';
import { MyLeaveRequests } from './pages/MyLeaveRequests';
import { UserSettings } from './pages/UserSettings';
import { Invoices } from './pages/accounting/Invoices';
import { Payments } from './pages/accounting/Payments';
import { Overdue } from './pages/accounting/Overdue';
import { AccountingReports } from './pages/accounting/AccountingReports';
import { ReminderConfig } from './pages/accounting/ReminderConfig';
import HourlyLeaveEntry from './pages/HourlyLeaveEntry';
import MonthlyLeaveReport from './pages/MonthlyLeaveReport';
import DocumentTranslation from './pages/DocumentTranslation';
import { Products } from './pages/pim/Products';
import { ProductForm } from './pages/pim/ProductForm';
import { ProductDetail } from './pages/pim/ProductDetail';
import { AttributeGroups } from './pages/pim/AttributeGroups';
import { ProductTypes } from './pages/pim/ProductTypes';
import { AttributesManagement } from './pages/pim/AttributesManagement';
import { ApiKeysManagement } from './pages/pim/ApiKeysManagement';
import { Categories } from './pages/pim/Categories';
import { CategoryForm } from './pages/pim/CategoryForm';
import { MediaManagement } from './pages/pim/MediaManagement';
import { WorkflowDashboardPage } from './pages/pim/WorkflowDashboardPage';
import { Settings as PimSettings } from './pages/pim/Settings';
import { ImportExport } from './pages/pim/ImportExport';
import { Translations } from './pages/pim/Translations';
import { MediaLibraries } from './pages/pim/MediaLibraries';
import { Orders } from './pages/orders/Orders';
import Transports from './pages/transport/Transports';
import Carriers from './pages/transport/Carriers';
import TransportArchive from './pages/transport/TransportArchive';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <NotificationProvider>
            <BrowserRouter>
              <NotificationContainer />
              <Toaster position="top-right" />
              <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/password-reset-success" element={<PasswordResetSuccess />} />
            <Route element={<ProtectedRoute />}>
              <Route element={<InvoiceOnlyRedirect><MainLayout /></InvoiceOnlyRedirect>}>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/leaves" element={<MyLeaveRequests />} />
                <Route path="/leaves/team" element={<TeamLeaveRequests />} />
                <Route path="/leaves/new" element={<NewLeaveRequest />} />
                <Route path="/leaves/hourly" element={<HourlyLeaveEntry />} />
                <Route path="/leaves/:id" element={<LeaveRequestDetail />} />
                <Route path="/leaves/:id/edit" element={<EditLeaveRequest />} />
                <Route path="/leave-types" element={<LeaveTypeManagement />} />
                <Route path="/team" element={<Team />} />
                <Route path="/calendar" element={<TeamCalendar />} />
                <Route path="/team-calendar" element={<TeamCalendar />} />
                <Route path="/reports" element={<Reports />} />
                <Route path="/reports/monthly-leave" element={<MonthlyLeaveReport />} />
                <Route path="/users" element={<UserManagement />} />
                <Route path="/users/new" element={<UserForm />} />
                <Route path="/users/:id/edit" element={<UserForm />} />
                <Route path="/users/:userId/nip-access" element={<UserNipManagement />} />
                <Route path="/users/:id" element={<UserRedirect />} />
                <Route path="/notifications" element={<NotificationCenter />} />
                <Route path="/system-notifications" element={<SystemNotifications />} />
                <Route path="/departments" element={<DepartmentManagement />} />
                <Route path="/substitutions" element={<Substitutions />} />
                <Route path="/roles" element={<RoleManagement />} />
                <Route path="/workflows" element={<WorkflowManagement />} />
                <Route path="/workflows/:id" element={<WorkflowDetail />} />
                <Route path="/settings" element={<UserSettings />} />
                
                {/* Accounting Routes */}
                <Route path="/accounting/invoices" element={<Invoices />} />
                <Route path="/accounting/payments" element={<Payments />} />
                <Route path="/accounting/overdue" element={<Overdue />} />
                <Route path="/accounting/reports" element={<AccountingReports />} />
                <Route path="/accounting/reminder-config" element={<ReminderConfig />} />
                
                {/* Document Translation Routes */}
                <Route path="/translate" element={<DocumentTranslation />} />
                
                {/* PIM Routes */}
                <Route path="/pim/products" element={<Products />} />
                <Route path="/pim/products/new" element={<ProductForm />} />
                <Route path="/pim/products/:id" element={<ProductDetail />} />
                <Route path="/pim/products/:id/edit" element={<ProductForm />} />
                <Route path="/pim/workflow/dashboard" element={<WorkflowDashboardPage />} />
                <Route path="/pim/categories" element={<Categories />} />
                <Route path="/pim/categories/new" element={<CategoryForm />} />
                <Route path="/pim/categories/:id/edit" element={<CategoryForm />} />
                <Route path="/pim/media" element={<MediaManagement />} />
                <Route path="/pim/media-libraries" element={<MediaLibraries />} />
                <Route path="/pim/translations" element={<Translations />} />
                <Route path="/pim/product-types" element={<ProductTypes />} />
                <Route path="/pim/attribute-groups" element={<AttributeGroups />} />
                <Route path="/pim/attributes" element={<AttributesManagement />} />
                <Route path="/pim/api-keys" element={<ApiKeysManagement />} />
                <Route path="/pim/import-export" element={<ImportExport />} />
                <Route path="/pim/settings" element={<PimSettings />} />

                {/* Orders & Transport */}
                <Route path="/orders" element={<Orders />} />
                <Route path="/transport" element={<Transports />} />
                <Route path="/transport/carriers" element={<Carriers />} />
                <Route path="/transport/archive" element={<TransportArchive />} />
                
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
              </Route>
            </Route>
            </Routes>
            </BrowserRouter>
          </NotificationProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
